import React, { useRef, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  Check,
  Clock,
  Download,
  Edit3,
  FileText,
  Hash,
  Info,
  Lock,
  Upload,
  User,
} from 'lucide-react';
import {
  OFFICE_EXTENSIONS,
  readOfficeMetadata,
  writeOfficeMetadata,
} from './metadata/officeMetadata';
import { readPdfMetadata, writePdfMetadata } from './metadata/pdfMetadata';

const SUPPORTED_EXTENSIONS = [...OFFICE_EXTENSIONS, '.pdf'];
const MAX_PDF_SIZE = 100 * 1024 * 1024;

const getExtension = (filename) => {
  const lower = filename.toLowerCase();
  return SUPPORTED_EXTENSIONS.find((extension) => lower.endsWith(extension)) || '';
};

const OFFICE_FIELDS = [
  { key: 'creator', label: '作者（创建者）', placeholder: '输入作者姓名', icon: User },
  { key: 'lastModifiedBy', label: '最后修改者', placeholder: '输入最后修改者', icon: User },
  { key: 'created', label: '内容创建日期', type: 'datetime-local', icon: Calendar },
  { key: 'modified', label: '最后保存日期', type: 'datetime-local', icon: Calendar },
  { key: 'revision', label: '修订版本号', placeholder: '输入修订版本号', icon: Hash },
  { key: 'totalTime', label: '总编辑时间（分钟，仅 Word 适用）', placeholder: '输入总编辑时间', icon: Clock },
  { key: 'company', label: '公司', placeholder: '输入公司名称' },
  { key: 'application', label: '应用程序', placeholder: '输入应用程序名称' },
];

const PDF_FIELDS = [
  { key: 'title', label: '标题', placeholder: '输入 PDF 标题', icon: FileText },
  { key: 'author', label: '作者', placeholder: '输入作者姓名', icon: User },
  { key: 'subject', label: '主题', placeholder: '输入文档主题' },
  { key: 'keywords', label: '关键词', placeholder: '使用逗号分隔关键词' },
  { key: 'creator', label: '创建应用程序', placeholder: '例如 Microsoft Word' },
  { key: 'producer', label: 'PDF 生成器', placeholder: '例如 Adobe PDF Library' },
  { key: 'created', label: '创建日期', type: 'datetime-local', icon: Calendar },
  { key: 'modified', label: '修改日期', type: 'datetime-local', icon: Calendar },
];

const WeChatPayment = ({ onPaymentSuccess }) => {
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const paymentAmount = '20';

  const handlePaymentConfirmation = () => {
    if (!paymentConfirmed) {
      alert('请确认您已完成支付');
      return;
    }
    onPaymentSuccess({
      transactionId: `WECHAT-${Date.now()}`,
      amount: paymentAmount,
      timestamp: new Date().toISOString(),
      method: '微信支付',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 p-4 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-500 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-2xl">微</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">微信支付</h2>
          <p className="text-gray-600">使用微信扫描二维码支付</p>
        </div>

        <div className="mb-6 p-4 bg-green-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium">Office 与 PDF 编辑器访问权限</span>
            <span className="text-xl font-bold text-green-600">¥{paymentAmount}</span>
          </div>
        </div>

        <div className="mb-6">
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6 text-center">
            <img
              src="/wechat-qr.png"
              alt="微信支付二维码"
              className="w-80 max-w-full mx-auto rounded-lg border-2 border-gray-200 mb-4"
            />
            <p className="text-sm text-gray-600">使用微信应用扫描支付 ¥{paymentAmount}</p>
          </div>
        </div>

        {showInstructions && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">支付方式：</h4>
            <ol className="text-sm text-blue-700 space-y-1">
              <li>1. 打开微信应用</li>
              <li>2. 点击“扫一扫”或“+”</li>
              <li>3. 扫描上方二维码</li>
              <li>4. 在微信中完成支付</li>
              <li>5. 返回此处确认支付</li>
            </ol>
            <button
              onClick={() => setShowInstructions(false)}
              className="text-xs text-blue-600 hover:text-blue-800 mt-2"
            >
              隐藏说明
            </button>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="payment-confirmed"
              checked={paymentConfirmed}
              onChange={(event) => setPaymentConfirmed(event.target.checked)}
              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
            />
            <label htmlFor="payment-confirmed" className="text-sm font-medium text-gray-700">
              我已完成微信支付
            </label>
          </div>
          <button
            onClick={handlePaymentConfirmation}
            disabled={!paymentConfirmed}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Check className="w-5 h-5" />
            确认支付并继续
          </button>
        </div>

        <div className="mt-6 text-xs text-gray-500 text-center">
          <Lock className="w-4 h-4 inline mr-1" />
          微信支付安全交易
        </div>
      </div>
    </div>
  );
};

const AccessGranted = ({ paymentData, onContinue }) => (
  <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 p-4 flex items-center justify-center">
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
      <Check className="w-16 h-16 mx-auto mb-4 text-green-600" />
      <h2 className="text-2xl font-bold text-gray-800 mb-4">支付成功！</h2>
      <p className="text-gray-600 mb-6">您的支付已确认。现在可以访问 Office 与 PDF 元数据编辑器。</p>
      {paymentData && (
        <div className="mb-6 p-4 bg-green-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">支付详情</p>
          <p className="font-medium text-lg">¥{paymentData.amount}</p>
          <p className="text-sm text-gray-500">交易号：{paymentData.transactionId}</p>
          <p className="text-xs text-gray-400 mt-1">{paymentData.method}</p>
        </div>
      )}
      <button
        onClick={onContinue}
        className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
      >
        <FileText className="w-5 h-5" />
        访问文档编辑器
      </button>
    </div>
  </div>
);

const MetadataField = ({ field, value, onChange }) => {
  const Icon = field.icon;
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
        {Icon && <Icon className="w-4 h-4" />}
        {field.label}
      </label>
      <input
        type={field.type || 'text'}
        value={value || ''}
        onChange={(event) => onChange(field.key, event.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder={field.placeholder}
      />
    </div>
  );
};

const PdfTechnicalDetails = ({ details }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-lg bg-slate-50 border border-slate-200 p-4">
    <div>
      <p className="text-xs text-slate-500">页数</p>
      <p className="font-medium text-slate-800">{details.pageCount}</p>
    </div>
    <div>
      <p className="text-xs text-slate-500">文件大小</p>
      <p className="font-medium text-slate-800">{details.fileSize}</p>
    </div>
    <div>
      <p className="text-xs text-slate-500">加密</p>
      <p className="font-medium text-slate-800">{details.encrypted ? '是' : '否'}</p>
    </div>
    <div>
      <p className="text-xs text-slate-500">数字签名</p>
      <p className={`font-medium ${details.signed ? 'text-amber-700' : 'text-slate-800'}`}>
        {details.signed ? '已检测到' : '未检测到'}
      </p>
    </div>
  </div>
);

const AdvancedPdfMetadata = ({ entries, onChange }) => {
  if (!entries?.length) return null;
  return (
    <details className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <summary className="cursor-pointer font-medium text-slate-800">
        高级 PDF 元数据（{entries.length} 项）
      </summary>
      <p className="mt-2 text-xs text-slate-500">
        Info 字典中的文本值可以编辑；复杂 XMP、数组和结构值仅显示，以避免损坏文件。
      </p>
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {entries.map((entry, index) => (
          <div className="space-y-1" key={`${entry.source}-${entry.key}-${index}`}>
            <label className="text-xs font-medium text-slate-600">
              {entry.key} <span className="font-normal">({entry.source})</span>
            </label>
            <input
              type="text"
              value={entry.value}
              readOnly={!entry.editable}
              onChange={(event) => onChange(index, event.target.value)}
              className={`w-full px-3 py-2 border rounded-md text-sm ${
                entry.editable
                  ? 'border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'
                  : 'border-slate-200 bg-slate-100 text-slate-500'
              }`}
            />
          </div>
        ))}
      </div>
    </details>
  );
};

const DocumentMetadataEditor = () => {
  const [file, setFile] = useState(null);
  const [fileKind, setFileKind] = useState('');
  const [metadata, setMetadata] = useState({});
  const [source, setSource] = useState(null);
  const [technical, setTechnical] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const resetDocument = () => {
    setFile(null);
    setFileKind('');
    setMetadata({});
    setSource(null);
    setTechnical(null);
  };

  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files?.[0];
    const extension = uploadedFile ? getExtension(uploadedFile.name) : '';
    setError('');

    if (!uploadedFile || !extension) {
      resetDocument();
      setError('请选择有效的 Office 或 PDF 文件（.docx / .xlsx / .xlsm / .pptx / .pdf）。');
      return;
    }
    if (extension === '.pdf' && uploadedFile.size > MAX_PDF_SIZE) {
      resetDocument();
      setError('PDF 文件不能超过 100 MB，以避免浏览器内存不足。');
      return;
    }

    setLoading(true);
    resetDocument();
    try {
      const result =
        extension === '.pdf'
          ? await readPdfMetadata(uploadedFile)
          : await readOfficeMetadata(uploadedFile);
      setFile(uploadedFile);
      setFileKind(extension === '.pdf' ? 'pdf' : 'office');
      setMetadata(result.metadata);
      setSource(result.source);
      setTechnical(result.technical || null);
    } catch (uploadError) {
      console.error('Error reading document:', uploadError);
      setError(uploadError.message || '读取文档时出错。');
      event.target.value = '';
    } finally {
      setLoading(false);
    }
  };

  const handleMetadataChange = (field, value) => {
    setMetadata((current) => ({ ...current, [field]: value }));
  };

  const handleAdvancedChange = (index, value) => {
    setMetadata((current) => ({
      ...current,
      customInfo: current.customInfo.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, value } : entry
      ),
    }));
  };

  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const downloadModifiedDocument = async () => {
    if (!source || !file) {
      setError('未加载文件。请先上传文档。');
      return;
    }
    if (
      fileKind === 'pdf' &&
      source.signed &&
      !window.confirm('此 PDF 包含数字签名。修改元数据会使现有签名失效。仍要继续吗？')
    ) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const extension = getExtension(file.name);
      const outputName = `${file.name.slice(0, -extension.length)}_modified${extension}`;
      if (fileKind === 'pdf') {
        const bytes = await writePdfMetadata(source, metadata);
        downloadBlob(new Blob([bytes], { type: 'application/pdf' }), outputName);
      } else {
        const blob = await writeOfficeMetadata(source, metadata);
        downloadBlob(blob, outputName);
      }
    } catch (downloadError) {
      console.error('Error modifying document:', downloadError);
      setError(`文档修改时出错：${downloadError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fields = fileKind === 'pdf' ? PDF_FIELDS : OFFICE_FIELDS;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8" />
              <h1 className="text-2xl font-bold">Office 与 PDF 元数据编辑器</h1>
            </div>
            <p className="opacity-90">在浏览器中查看和修改 Office、PDF 文档属性，文件不会上传到服务器</p>
          </div>

          <div className="p-6">
            <div className="mb-8">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx,.xlsx,.xlsm,.pptx,.pdf,application/pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold mb-2">上传 Office 或 PDF 文档</h3>
                <p className="text-gray-600 mb-4">支持 .docx / .xlsx / .xlsm / .pptx / .pdf</p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? '处理中...' : '选择文件'}
                </button>
                {file && <p className="mt-3 text-sm text-gray-600">已选择：{file.name}</p>}
              </div>
            </div>

            {error && (
              <div role="alert" className="mb-6 flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                <AlertTriangle className="w-5 h-5 flex-none mt-0.5" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {file && !loading && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Edit3 className="w-5 h-5" />
                  {fileKind === 'pdf' ? 'PDF 文档属性' : 'Office 文档属性'}
                </h2>

                {fileKind === 'pdf' && technical && <PdfTechnicalDetails details={technical} />}
                {technical?.signed && (
                  <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
                    <AlertTriangle className="w-5 h-5 flex-none" />
                    <p className="text-sm">此 PDF 包含数字签名。下载修改后的副本会使现有签名失效。</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  {fields.map((field) => (
                    <MetadataField
                      key={field.key}
                      field={field}
                      value={metadata[field.key]}
                      onChange={handleMetadataChange}
                    />
                  ))}
                </div>

                {fileKind === 'pdf' && (
                  <AdvancedPdfMetadata
                    entries={metadata.customInfo}
                    onChange={handleAdvancedChange}
                  />
                )}

                <button
                  onClick={downloadModifiedDocument}
                  disabled={loading}
                  className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                >
                  <Download className="w-5 h-5" />
                  {loading ? '处理中...' : '下载修改后的文档'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-3">使用方法：</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>上传 Office 文档或 PDF 文件</li>
            <li>查看并编辑该格式支持的文档属性</li>
            <li>点击“下载修改后的文档”获取新副本</li>
            <li>原文件保持不变，新文件名将包含“_modified”</li>
          </ol>
          <div className="mt-4 flex gap-3 rounded-lg bg-blue-50 p-4 text-blue-700">
            <Info className="w-5 h-5 flex-none" />
            <p className="text-sm">所有处理均在您的浏览器中完成，文件不会上传到服务器。</p>
          </div>
          <div className="mt-4 p-3 bg-amber-50 rounded-lg">
            <p className="text-sm text-amber-700">
              ⚠️ Office 仅支持基于 ZIP 的 2007 及以后格式。加密或受密码保护的 PDF 无法修改；修改已签名 PDF 会使其数字签名失效。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AppWithAccessControl = () => {
  const [accessState, setAccessState] = useState('payment');
  const [paymentData, setPaymentData] = useState(null);

  if (accessState === 'payment') {
    return (
      <WeChatPayment
        onPaymentSuccess={(payment) => {
          setPaymentData(payment);
          setAccessState('success');
        }}
      />
    );
  }
  if (accessState === 'success') {
    return <AccessGranted paymentData={paymentData} onContinue={() => setAccessState('app')} />;
  }
  return <DocumentMetadataEditor />;
};

export default AppWithAccessControl;
